import type mdast from 'mdast';
import last from 'lodash-es/last';
import { NBSP } from '../textToHtml';
import { collapseInlineWhitespace } from '../whitespace';

export class MarkdownParentBuffer<T extends mdast.Content & mdast.Parent, U extends mdast.Content = T> {
  protected readonly parent: T;
  private children: T['children'];

  constructor(parent: T) {
    this.parent = parent;
    this.children = [];
  }

  push(child: T['children'][0]) {
    this.children.push(child);
  }

  protected flushChildren(): T['children'] | undefined {
    const { children } = this;
    this.children = [];

    if (children.length === 0) {
      return undefined;
    }

    return children.reduce((prevChildren, child) => {
      const prevChild = last(prevChildren);

      // Try to merge adjacent children.
      const mergedChild = maybeMergeChildren(prevChild, child);
      if (mergedChild) {
        return [...prevChildren.slice(0, -1), mergedChild];
      }

      return [...prevChildren, child];
    }, [] as T['children']);
  }

  *flush(): Iterable<T | U> {
    const children = this.flushChildren();

    if (!children) {
      return;
    }

    yield {
      ...this.parent,
      children,
    };
  }
}

function maybeMergeChildren(
  prevChild: mdast.Content | undefined,
  nextChild: mdast.Content,
): mdast.Content | undefined {
  if (!prevChild) {
    return undefined;
  }

  switch (prevChild.type) {
    case 'text':
    case 'inlineCode':
      if (nextChild.type !== prevChild.type) {
        return undefined;
      }

      return {
        ...prevChild,
        value: mergeTextValues(prevChild.value, nextChild.value),
      };

    case 'emphasis':
    case 'strong':
    case 'delete':
      if (nextChild.type !== prevChild.type) {
        return undefined;
      }

      return {
        ...prevChild,
        children: [...prevChild.children, ...nextChild.children],
      };

    case 'link':
      if (
        nextChild.type !== prevChild.type ||
        nextChild.url !== prevChild.url ||
        nextChild.title !== prevChild.title
      ) {
        return undefined;
      }

      return {
        ...prevChild,
        children: [...prevChild.children, ...nextChild.children],
      };

    default:
      return undefined;
  }
}

function mergeTextValues(prevText: string, nextText: string): string {
  const maybeExpandedNextText = hasTrailingWhitespace(prevText)
    ? expandLeadingWhitespace(nextText)
    : nextText;
  return collapseInlineWhitespace(`${prevText}${maybeExpandedNextText}`);
}

function hasTrailingWhitespace(text: string) {
  return /[ ]$/.test(text);
}

function expandLeadingWhitespace(text: string) {
  return text.replace(/^[ ]/, NBSP);
}
